import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ProfileSidebar from '../../components/Profile/ProfileSidebar';
import ProfileMobileSubNav from '../../components/Profile/ProfileMobileSubNav';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { FaHouse } from 'react-icons/fa6';
import { MdOutlineClose } from 'react-icons/md';
import AddNewAddressModal from '../../components/CheckOut/AddNewAddressModal';
import { useUser } from '../../contexts/user';
import { useCart } from '../../contexts/cart';
import { useNavigate } from 'react-router-dom';
import log from '../../utils/logger';
import { getApiErrorMessage } from '../../utils/apiError';
import api from '../../api/api';
import useToast from '../../hooks/useToast';
import Toast from '../../components/Toast';

const ManageAddress = () => {
  const { t } = useTranslation();
  const { toastMessage, toastType, showToast } = useToast();
  const [isAddNewAddressModalOpen, setIsAddNewAddressModalOpen] =
    useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { user, idToken, updateProfile, logout, adminImpersonate } = useUser();
  const { clearCart } = useCart();
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const storedAddresses = user?.locations;

    if (Array.isArray(storedAddresses)) {
      const filtered = storedAddresses.filter((addr) => addr != null);
      setAddresses(filtered);
    } else {
      setAddresses([]);
    }
  }, [user?.locations]);

  const handleAddNewAddressModal = () => {
    setIsAddNewAddressModalOpen(!isAddNewAddressModalOpen);
    setIsEdit(false);
    setSelectedAddress(null);
  };

  const handleDeleteModal = (address = null) => {
    setSelectedAddress(address);
    setIsDeleteModalOpen(!isDeleteModalOpen);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedAddress(null);
  };

  const handleEditAddress = (address) => {
    setSelectedAddress(address);
    setIsAddNewAddressModalOpen(true);
    setIsEdit(true);
  };

  const handleAddressUpdated = (newLocations) => {
    setAddresses(newLocations);
    setSelectedAddress(null);
  };

  const handleDeleteAddress = async () => {
    if (!selectedAddress || !selectedAddress._id) {
      showToast(t('noAddressSelected'), 'warning');
      return;
    }
    if (!idToken && !adminImpersonate) {
      showToast(t('userNotAuthenticated'), 'error');
      return;
    }

    try {
      const response = await api.delete('/user/locations', {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
        data: {
          _id: selectedAddress._id,
          adminImpersonateToken: adminImpersonate || null,
          buyerEmail: user?.email,
          buyerId: user?.id,
        },
      });
      setAddresses(
        addresses.filter((addr) => addr._id !== selectedAddress._id),
      );
      const locations = response.data.response.data.locations;
      updateProfile({ locations: locations });
      handleCloseDeleteModal();
    } catch (error) {
      log.error('Error deleting address:', error);
      const message = error.response?.data?.error || '';
      if (message === 'TOKEN_EXPIRED' || message === 'INVALID_ID_TOKEN') {
        navigate('/home');
        sessionStorage.setItem('sessionExpired', 'true');
        sessionStorage.setItem(
          'toast',
          JSON.stringify({
            message: t('sessionExpiredMessage'),
            type: 'error',
          }),
        );
        logout();
        clearCart();
      } else {
        showToast(getApiErrorMessage(error, t), 'error');
      }
    }
  };

  if ((!idToken || !user) && !adminImpersonate) {
    return (
      <>
        <div className="flex flex-col min-h-dvh">
          <Header />
          <ProfileMobileSubNav />
          <section className="pt-20 lg:px-3 pb-20 lg:pb-40">
            <div className="max-w-[1220px] w-full mx-auto flex gap-4 lg:gap-20">
              <div className="hidden lg:block">
                <ProfileSidebar />
              </div>
              <div className="flex-1 flex justify-center items-center">
                <p className="text-[#999]">{t('loginRequiredMessage')}</p>
              </div>
            </div>
          </section>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col min-h-dvh">
        <Header />
        <ProfileMobileSubNav />

        <section className="pt-5 lg:pt-20 px-3 pb-20 lg:pb-40">
          <div className="max-w-[1220px] w-full mx-auto flex gap-4 lg:gap-20">
            <div className="hidden lg:block">
              <ProfileSidebar />
            </div>

            <div className="flex-1 elevation-2 rounded h-fit">
              <div className="hidden lg:flex items-center justify-between p-4">
                <h2 className="font-semibold text-xl text-[#000000DE]">
                  {t('Manage addresses')}
                </h2>
                <button
                  onClick={handleAddNewAddressModal}
                  className={
                    'h-9 px-4 text-center bg-[#0b1223] text-white text-base capitalize rounded font-medium transition-opacity duration-300 hover:bg-opacity-90'
                  }
                >
                  {t('Add Address')}
                </button>
              </div>

              {/* Render addresses dynamically */}
              {addresses.length === 0 && (
                <p className="p-4 text-center text-gray-500">
                  {t('No addresses found')}
                </p>
              )}

              {(addresses || [])
                .filter((addr) => addr && addr.formattedAddress)
                .map((addr, idx) => (
                  <div
                    key={idx}
                    className="py-4 px-4 md:px-8 flex gap-4 md:gap-8 border-b border-gray-300"
                  >
                    <FaHouse className="mt-1 text-[#666666]" />
                    <div className="flex-1">
                      <h2 className="font-semibold capitalize text-base">
                        {t(addr.addressType) || `${t('address')} ${idx + 1}`}
                      </h2>
                      <p className="text-sm text-[#666666]">
                        {addr.addressNumber ? (
                          <div>
                            {addr.addressNumber},&nbsp;{addr.formattedAddress}
                          </div>
                        ) : (
                          <div>{addr.formattedAddress}</div>
                        )}
                      </p>
                      <p className="text-sm text-[#666666] py-[2px]">
                        {t('Contact')}: {addr.phoneNumber}
                      </p>

                      <div className="flex gap-4 mt-4">
                        <button
                          onClick={() => handleEditAddress(addr)}
                          className="text-[#0671E3] text-sm uppercase font-semibold"
                        >
                          {t('Edit')}
                        </button>
                        <button
                          onClick={() => handleDeleteModal(addr)}
                          className="text-[#0671E3] text-sm uppercase font-semibold"
                        >
                          {t('Delete')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

              {/* Add Address Button for Mobile */}
              <div className="lg:hidden fixed bottom-4 left-0 w-full px-4 z-50">
                <button
                  onClick={handleAddNewAddressModal}
                  className="w-full h-12 bg-[#0b1223] text-white text-base font-medium rounded-lg shadow-md hover:bg-opacity-90 transition"
                >
                  {t('Add Address')}
                </button>
              </div>
            </div>
          </div>

          {isAddNewAddressModalOpen && (
            <AddNewAddressModal
              onClose={handleAddNewAddressModal}
              isAddNewAddressModalOpen={isAddNewAddressModalOpen}
              initialAddress={selectedAddress}
              onAddressUpdated={handleAddressUpdated}
              isEdit={isEdit}
            />
          )}

          {/* Confirm Delete Modal */}
          {isDeleteModalOpen && (
            <div className="bg-[#212121] bg-opacity-[0.46] fixed top-0 left-0 w-full h-dvh flex items-center justify-center px-4">
              <div className="bg-white pt-4 px-6 pb-6 max-w-[500px] mx-auto w-full rounded-lg">
                <div className="flex items-center justify-between pt-5 pb-3 text-base font-medium">
                  {t('Delete Address')}
                  <button onClick={handleCloseDeleteModal}>
                    <MdOutlineClose className="text-xl" />
                  </button>
                </div>

                <div className="p-3">
                  <div className="text-base text-[#000000DE] mb-5 text-center">
                    {t('Are you sure you want to Delete Address')}{' '}
                    <span className="text-[#F44336]">
                      "{selectedAddress?.formattedAddress || ''}"
                    </span>{' '}
                    ?
                  </div>

                  <div className="flex gap-2 justify-center">
                    <button
                      onClick={handleDeleteAddress}
                      className={
                        'h-9 px-4 text-center bg-[#0b1223] text-white text-sm uppercase rounded font-medium transition-opacity duration-300 hover:bg-opacity-90'
                      }
                    >
                      {t('Yes')}
                    </button>
                    <button
                      onClick={handleCloseDeleteModal}
                      className={
                        'h-9 px-4 text-center bg-[#0b1223] text-white text-sm uppercase rounded font-medium transition-opacity duration-300 hover:bg-opacity-90'
                      }
                    >
                      {t('No')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
      <Footer />
      <Toast message={toastMessage} type={toastType} />
    </>
  );
};

export default ManageAddress;
